/**
 * Quais ações concluídas podem pedir a pesquisa de NPS — e em qual módulo.
 *
 * POR QUE POR EVENTO, E NÃO POR TEMPO
 * A primeira versão disparava por permanência: 15 s parado numa rota do
 * módulo. Em produção (11–14/09/2026) isso deu 37 dispensas e 0 respostas —
 * 31 delas no meio do fechamento de comissão. Quinze segundos numa tela de
 * trabalho é o normal, não o fim de uma tarefa. Pedir a nota DEPOIS de a
 * pessoa concluir o que veio fazer é o único momento em que ela tem uma
 * opinião formada e tempo para dá-la.
 *
 * DE ONDE VEM O SINAL
 * Os handlers de sucesso já emitem `$gtagEvent` com nome de negócio
 * (`comissoes_fechamento_aprovar`, `tesouraria_solicitacao_aprovada`…), e o
 * shell espelha todo `$gtagEvent` em `GlobalBus` como `TELEMETRIA.EVENTO`.
 * Escutar isso cobre seis módulos sem tocar em nenhum handler. Dois sinais
 * que não passam pelo gtag (`RELOAD.CLIENT_CREATED`, `ACTION.UPDATE_ASSET`)
 * são ouvidos direto no bus.
 *
 * A CHAVE
 * `eventName` do gtag quando existe e não é o default `'click'`; senão
 * `module.action` — os eventos do CRM só passam `action`.
 *
 * O MÓDULO É O DA ROTA, NUNCA O DO GTAG
 * `dataroom_importar` sai com `module: 'bi'`; o modal de oportunidade é o
 * mesmo em `crm.*` e `opportunities.*`. A pergunta "Que nota você dá para
 * ___?" tem que casar com a tela em que a pessoa está — por isso cada evento
 * lista os módulos em que VALE, e o efetivo é o da rota atual.
 */
import { ACTION, RELOAD } from '@constants/Text'
import { MODULOS_POR_RAIZ, moduloDaRota } from '@modules/nps/constants/modulos'

export const GATILHOS_POR_EVENTO = {
  comissoes_fechamento_aprovar: { modulos: ['comissoes'] },
  tesouraria_solicitacao_nova: { modulos: ['tesouraria'] },
  tesouraria_solicitacao_aprovada: { modulos: ['tesouraria'] },
  'crm.concluir_tarefa_lead': { modulos: ['crm'] },
  'crm.atualizar_oportunidade': { modulos: ['crm', 'oportunidades'] },
  'crm.adicionar_lead': { modulos: ['crm'] },
  boletador_boletar: { modulos: ['boletador'] },
  dataroom_importar: { modulos: ['data_room'] },
  // `simulador_simular` é o INÍCIO: a simulação roda por polling e a
  // conclusão é a chegada em `simulador.analise`. O evento arma; a rota decide.
  simulador_simular: {
    modulos: ['simulador'],
    aguardaRota: 'simulador.analise',
  },
  [RELOAD.CLIENT_CREATED]: { modulos: ['clientes'] },
  [ACTION.UPDATE_ASSET]: { modulos: ['consolidador'] },
}

/** Sinais que o NPS assina no GlobalBus além de `TELEMETRIA.EVENTO`. */
export const SINAIS_DO_BUS = [RELOAD.CLIENT_CREATED, ACTION.UPDATE_ASSET]

/**
 * Rotas de módulos COM evento onde não há tarefa concluível — só leitura.
 * Casam por prefixo. Nelas vale a permanência longa.
 */
export const ROTAS_SO_LEITURA = [
  'comissoes.visao-geral',
  'comissoes.roa',
  'comissoes.pagamentos',
]

export const MODO_EVENTO = 'evento'
export const MODO_PERMANENCIA = 'permanencia'

/** Módulos que têm ao menos um evento — derivado, não mantido à mão. */
export const MODULOS_POR_EVENTO = new Set(
  Object.values(GATILHOS_POR_EVENTO).reduce(
    (todos, gatilho) => todos.concat(gatilho.modulos),
    []
  )
)

/**
 * @param {{module?: string, action?: string, eventName?: string}} evento
 * @returns {string|null}
 */
export function chaveDoEvento(evento) {
  const dados = evento || {}
  if (dados.eventName && dados.eventName !== 'click') return dados.eventName
  if (dados.module && dados.action) return dados.module + '.' + dados.action
  return null
}

/**
 * Como a rota dispara a pesquisa.
 *
 * Sem lista manual de permanência: módulo com evento na allowlist usa evento;
 * o resto (home, BI, tarefas, oportunidades) usa permanência. Acrescentar um
 * evento tira o módulo da permanência sozinho — que é a intenção.
 *
 * @returns {'evento'|'permanencia'|null} null quando a rota não é avaliada
 */
export function modoDaRota(nomeDaRota) {
  const modulo = moduloDaRota(nomeDaRota)
  if (!modulo) return null

  const nome = String(nomeDaRota)
  const soLeitura = ROTAS_SO_LEITURA.some(
    (prefixo) => nome.indexOf(prefixo) === 0
  )
  if (soLeitura) return MODO_PERMANENCIA

  return MODULOS_POR_EVENTO.has(modulo) ? MODO_EVENTO : MODO_PERMANENCIA
}

/**
 * Módulo a avaliar para este evento, ou null se o evento não vale na rota
 * atual (ex.: modal do CRM aberto a partir da home).
 */
export function moduloDoEvento(chave, moduloDaRotaAtual) {
  const gatilho = GATILHOS_POR_EVENTO[chave]
  if (!gatilho || !moduloDaRotaAtual) return null
  return gatilho.modulos.indexOf(moduloDaRotaAtual) >= 0
    ? moduloDaRotaAtual
    : null
}

/** Rota que confirma a conclusão, quando o evento é só o início. */
export function rotaQueConfirma(chave) {
  const gatilho = GATILHOS_POR_EVENTO[chave]
  return (gatilho && gatilho.aguardaRota) || null
}

// Guarda de consistência em tempo de import: módulo na allowlist que não
// existe no mapa de rotas nunca dispararia — melhor quebrar o build de teste
// do que descobrir em produção.
const MODULOS_CONHECIDOS = new Set(Object.values(MODULOS_POR_RAIZ))
MODULOS_POR_EVENTO.forEach((modulo) => {
  if (!MODULOS_CONHECIDOS.has(modulo)) {
    throw new Error(
      '[nps] módulo "' +
        modulo +
        '" da allowlist de eventos não existe em MODULOS_POR_RAIZ'
    )
  }
})
